import {
  IntranetContentKind,
  PermissionCode,
  addBerlinDays,
  berlinWeek,
  type IntranetCategory,
  type IntranetFeedItem,
} from '@gb-ticket/shared-types';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  LinkIcon,
  Loader2Icon,
  PlusIcon,
  TagsIcon,
  UsersIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { EditorialCalendarCard } from '@/products/intranet/components/editorial-calendar-card';
import { intranetPanelClassName } from '@/products/intranet/features/intranet/intranet-panel';
import {
  contributionCountLabel,
  editorialCalendarSearch,
  editorialColumnLabel,
  editorialComposePath,
  editorialDayHeading,
  editorialDays,
  editorialMonthChoices,
  editorialMonthLabel,
  editorialMonthValue,
  editorialPlanLabel,
  editorialPlacementIso,
  editorialRange,
  editorialToday,
  editorialVisibleSpan,
  editorialVisibleView,
  editorialWeekLabel,
  readEditorialCalendar,
  shiftEditorialAnchor,
  type EditorialCalendarQuery,
  type EditorialCalendarView,
} from '@/products/intranet/features/intranet/editorial-calendar';
import {
  useIntranetCategories,
  useIntranetContents,
} from '@/products/intranet/features/intranet/use-intranet';
import {
  INTRANET_CATEGORIES_PATH,
  INTRANET_EDITORIAL_APPEARANCE_PATH,
  INTRANET_EDITORIAL_CONTACTS_PATH,
  INTRANET_EDITORIAL_SETTINGS_PATH,
  INTRANET_EDITORIAL_SHORTCUTS_PATH,
} from '@/products/intranet/intranet-paths';

const CALENDAR_DESKTOP = '(min-width: 1024px)';

function useCalendarDesktop(): boolean {
  const [desktop, setDesktop] = useState(() => {
    if (typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(CALENDAR_DESKTOP).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(CALENDAR_DESKTOP);
    const onChange = () => setDesktop(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return desktop;
}

function monthPad(monthStart: string): number {
  const week = berlinWeek(monthStart);
  if (!week) {
    return 0;
  }
  let pad = 0;
  let cursor = week.start;
  while (cursor < monthStart) {
    pad += 1;
    const next = addBerlinDays(cursor, 1);
    if (!next) {
      break;
    }
    cursor = next;
  }
  return pad;
}

export function EditorialDashboardPage() {
  usePageTitle('Redaktionskalender');
  const me = useMe();
  const desktop = useCalendarDesktop();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = editorialToday();
  const { query, dateWasInvalid } = readEditorialCalendar(searchParams, today);
  const categories = useIntranetCategories();
  const loadedCategories = categories.data?.items;
  const categoryMissing = Boolean(
    query.categoryId &&
    loadedCategories &&
    !loadedCategories.some((category) => category.id === query.categoryId),
  );
  const categoryId =
    categories.isError || categoryMissing ? undefined : (query.categoryId ?? undefined);
  const span = editorialVisibleSpan(query, desktop);
  const view = editorialVisibleView(query, desktop);
  const range = editorialRange(query.date, span);
  const contents = useIntranetContents({
    drafts: true,
    kind: IntranetContentKind.NEWS,
    categoryId,
    from: range.from,
    to: range.to,
    page: 1,
  });

  useEffect(() => {
    if (!dateWasInvalid) {
      return;
    }
    const next = readEditorialCalendar(searchParams, editorialToday()).query;
    setSearchParams(editorialCalendarSearch(next), { replace: true });
  }, [dateWasInvalid, searchParams, setSearchParams]);

  useEffect(() => {
    if (!categoryMissing) {
      return;
    }
    setSearchParams(editorialCalendarSearch({ ...query, categoryId: null }), { replace: true });
  }, [categoryMissing, query, setSearchParams]);

  function writeQuery(next: EditorialCalendarQuery) {
    setSearchParams(editorialCalendarSearch(next));
  }

  function plan(date: string | null) {
    return editorialComposePath({
      date,
      categoryId: categoryId ?? null,
      now: new Date(),
    });
  }

  const canManageCategories = hasPermission(me.data, PermissionCode.INTRANET_CATEGORIES_MANAGE);
  const canManageContacts = hasPermission(me.data, PermissionCode.INTRANET_CONTACTS_MANAGE);
  const canManageLinks = hasPermission(me.data, PermissionCode.INTRANET_LINKS_MANAGE);
  const channelOptions = [...(loadedCategories ?? [])].sort(
    (left, right) => left.sortOrder - right.sortOrder,
  );
  const days = editorialDays(
    contents.data?.items ?? [],
    range.from,
    range.to,
    editorialPlacementIso,
  );
  const weekLabel = span === 'week' ? editorialWeekLabel(query.date) : null;

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Redaktionskalender"
        description="Veröffentlichte und geplante Nachrichten."
        action={
          <div className="flex flex-wrap gap-2">
            {canManageCategories ? (
              <Button asChild variant="outline" size="sm">
                <Link to={INTRANET_CATEGORIES_PATH}>
                  <TagsIcon aria-hidden />
                  Channels
                </Link>
              </Button>
            ) : null}
            {canManageContacts ? (
              <Button asChild variant="outline" size="sm">
                <Link to={INTRANET_EDITORIAL_CONTACTS_PATH}>
                  <UsersIcon aria-hidden />
                  Ansprechpartner
                </Link>
              </Button>
            ) : null}
            <Button asChild variant="outline" size="sm">
              <Link to={INTRANET_EDITORIAL_APPEARANCE_PATH}>Erscheinungsbild</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={INTRANET_EDITORIAL_SETTINGS_PATH}>Einstellungen</Link>
            </Button>
            {canManageLinks ? (
              <Button asChild variant="outline" size="sm">
                <Link to={INTRANET_EDITORIAL_SHORTCUTS_PATH}>
                  <LinkIcon aria-hidden />
                  Schnellzugriff
                </Link>
              </Button>
            ) : null}
            <Button asChild size="sm">
              <Link to={plan(null)}>
                <PlusIcon aria-hidden />
                Beitrag planen
              </Link>
            </Button>
          </div>
        }
      />

      <div className={`${intranetPanelClassName} flex flex-wrap items-center gap-2`}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => writeQuery({ ...query, date: editorialToday() })}
        >
          Heute
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Vorheriger Zeitraum"
          onClick={() => writeQuery({ ...query, date: shiftEditorialAnchor(query.date, span, -1) })}
        >
          <ChevronLeftIcon aria-hidden />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Nächster Zeitraum"
          onClick={() => writeQuery({ ...query, date: shiftEditorialAnchor(query.date, span, 1) })}
        >
          <ChevronRightIcon aria-hidden />
        </Button>
        <Select
          value={editorialMonthValue(query.date)}
          onValueChange={(value) => writeQuery({ ...query, date: value })}
        >
          <SelectTrigger aria-label="Monat" className="w-auto min-w-40">
            <SelectValue>{editorialMonthLabel(query.date)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {editorialMonthChoices(query.date).map((choice) => (
              <SelectItem key={choice.value} value={choice.value}>
                {choice.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {weekLabel ? <p className="text-sm text-muted-foreground">{weekLabel}</p> : null}
        {desktop ? (
          <Select
            value={query.view}
            onValueChange={(value) => writeQuery(viewQuery(query, value as EditorialCalendarView))}
          >
            <SelectTrigger aria-label="Ansicht" className="w-auto min-w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Woche</SelectItem>
              <SelectItem value="month">Monat</SelectItem>
              <SelectItem value="agenda">Agenda</SelectItem>
            </SelectContent>
          </Select>
        ) : null}
        <Select
          value={categories.isError ? 'all' : (categoryId ?? 'all')}
          onValueChange={(value) =>
            writeQuery({ ...query, categoryId: value === 'all' ? null : value })
          }
        >
          <SelectTrigger aria-label="Channels" className="w-auto min-w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            {categories.isError
              ? null
              : channelOptions.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
          </SelectContent>
        </Select>
        {categories.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Die Channels konnten nicht geladen werden.
          </p>
        ) : null}
      </div>

      {contents.isPending ? (
        <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Beiträge werden geladen …
        </p>
      ) : contents.isError ? (
        <p role="alert" className="py-8 text-sm text-destructive">
          Die Beiträge konnten nicht geladen werden.
        </p>
      ) : (
        <div className="space-y-4">
          {view === 'agenda' ? (
            <AgendaDays
              days={days}
              categories={loadedCategories}
              categoriesPending={categories.isPending}
            />
          ) : null}
          {view === 'week' ? (
            <WeekColumns
              days={days}
              categories={loadedCategories}
              categoriesPending={categories.isPending}
              plan={plan}
            />
          ) : null}
          {view === 'month' ? (
            <MonthCells
              days={days}
              onPick={(date) => writeQuery({ ...query, view: 'week', span: 'week', date })}
            />
          ) : null}
        </div>
      )}
    </section>
  );
}

function viewQuery(
  query: EditorialCalendarQuery,
  view: EditorialCalendarView,
): EditorialCalendarQuery {
  if (view === 'week') {
    return { ...query, view, span: 'week' };
  }
  if (view === 'month') {
    return { ...query, view, span: 'month' };
  }
  return { ...query, view: 'agenda' };
}

function channelPresentation(
  item: IntranetFeedItem,
  categories: readonly IntranetCategory[] | undefined,
  categoriesPending: boolean,
): { name: string; icon: string | null } {
  if (!item.category) {
    return { name: 'Ohne Channel', icon: null };
  }
  const match = categories?.find((category) => category.id === item.category?.id);
  return {
    name: item.category.name,
    icon: categoriesPending ? null : (match?.iconName ?? null),
  };
}

function WeekColumns({
  days,
  categories,
  categoriesPending,
  plan,
}: {
  days: { date: string; items: IntranetFeedItem[] }[];
  categories: readonly IntranetCategory[] | undefined;
  categoriesPending: boolean;
  plan: (date: string) => string;
}) {
  return (
    <div className="grid grid-cols-7 items-stretch gap-2">
      {days.map((day) => {
        const count = contributionCountLabel(day.items.length);
        return (
          <div
            key={day.date}
            data-date={day.date}
            className="flex min-h-80 min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm"
          >
            <Link
              to={plan(day.date)}
              className="border-b border-border px-2 py-2 text-left hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              aria-label={editorialPlanLabel(day.date)}
            >
              <span className="block text-sm font-medium">{editorialColumnLabel(day.date)}</span>
              {count ? <span className="block text-xs text-muted-foreground">{count}</span> : null}
            </Link>
            <div className="flex flex-1 flex-col gap-2 bg-muted/40 p-2">
              {day.items.map((item) => {
                const channel = channelPresentation(item, categories, categoriesPending);
                return (
                  <EditorialCalendarCard
                    key={item.id}
                    item={item}
                    channelName={channel.name}
                    channelIcon={channel.icon}
                  />
                );
              })}
              <Link
                to={plan(day.date)}
                aria-label={editorialPlanLabel(day.date)}
                className="min-h-16 flex-1 rounded-md hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthCells({
  days,
  onPick,
}: {
  days: { date: string; items: IntranetFeedItem[] }[];
  onPick: (date: string) => void;
}) {
  const pad = days[0] ? monthPad(days[0].date) : 0;
  return (
    <div className="grid grid-cols-7 gap-2">
      {days.map((day, index) => {
        const count = contributionCountLabel(day.items.length);
        const titles = day.items.slice(0, 2).map((item) => item.title);
        return (
          <button
            key={day.date}
            type="button"
            data-date={day.date}
            style={index === 0 ? { gridColumnStart: pad + 1 } : undefined}
            className="min-h-24 rounded-md border border-border bg-card p-2 text-left hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            onClick={() => onPick(day.date)}
          >
            <span className="block text-sm font-medium">{editorialColumnLabel(day.date)}</span>
            {count ? <span className="block text-xs text-muted-foreground">{count}</span> : null}
            {titles.map((title) => (
              <span key={title} className="mt-1 block truncate text-xs">
                {title}
              </span>
            ))}
            {day.items.length > 2 ? (
              <span className="mt-1 block text-xs text-muted-foreground">weitere</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function AgendaDays({
  days,
  categories,
  categoriesPending,
}: {
  days: { date: string; items: IntranetFeedItem[] }[];
  categories: readonly IntranetCategory[] | undefined;
  categoriesPending: boolean;
}) {
  const filled = days.filter((day) => day.items.length > 0);
  if (filled.length === 0) {
    const month = days.length > 7;
    return (
      <p className={`${intranetPanelClassName} px-4 py-8 text-sm text-muted-foreground`}>
        {month ? 'Keine Beiträge in diesem Monat.' : 'Keine Beiträge in dieser Woche.'}
      </p>
    );
  }
  return (
    <div className="space-y-6">
      {filled.map((day) => {
        const count = contributionCountLabel(day.items.length);
        return (
          <section
            key={day.date}
            className="space-y-3 rounded-xl border border-border bg-card p-3 shadow-sm"
          >
            <h2 className="border-b border-border pb-2 text-sm font-medium">
              {editorialDayHeading(day.date)}
              {count ? (
                <span className="ml-2 font-normal text-muted-foreground">{count}</span>
              ) : null}
            </h2>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {day.items.map((item) => {
                const channel = channelPresentation(item, categories, categoriesPending);
                return (
                  <EditorialCalendarCard
                    key={item.id}
                    item={item}
                    channelName={channel.name}
                    channelIcon={channel.icon}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
